/**
 * BVED Hauptkategorien (Kostenarten 1-17)
 * Basierend auf der BVED-Kostenschlüsselliste V1.0
 *
 * Die Hauptkategorien entsprechen der ersten Ziffer im BVED-Key (z.B. "6.2.16" → Kategorie 6)
 */
export enum BvedMainCategory {
	PUBLIC_CHARGES = 1, // Lfd. Öffentliche Lasten
	WATER_SUPPLY_SEPARATE = 2, // Wasserversorgung und Entwässerung (getrennt)
	WATER_SUPPLY_COMBINED = 3, // Wasserversorgung und Entwässerung (verbunden)
	CENTRAL_HEATING = 4, // Zentrale Heizanlage
	CENTRAL_HOT_WATER = 5, // Zentrale Warmwasserversorgungsanlage
	COMBINED_HEATING_HOT_WATER = 6, // Verbundene Heizungs- und Warmwasserversorgungsanlagen
	ELEVATOR = 7, // Fahrstuhl / Aufzug
	STREET_CLEANING_WASTE = 8, // Straßenreinigung / Müllgebühren
	BUILDING_CLEANING = 9, // Gebäudereinigung / Ungezieferbekämpfung
	GARDEN_MAINTENANCE = 10, // Gartenpflege
	LIGHTING = 11, // Beleuchtung
	CHIMNEY_CLEANING = 12, // Schornsteinreinigung
	INSURANCE = 13, // Sach- und Haftpflichtversicherung
	CARETAKER = 14, // Hauswart
	ANTENNA = 15, // Antennenanlage / Breitbandkabelanschluss
	LAUNDRY_FACILITIES = 16, // Einrichtungen für die Wäschepflege
	OTHER_COSTS = 17, // Sonstige Betriebskosten
}

/**
 * Utility-Funktionen für BVED Hauptkategorien
 */

/**
 * Gibt alle definierten Hauptkategorien als Array zurück
 * @returns Array mit allen Hauptkategorien (dynamisch aus dem Enum ermittelt)
 *
 * @example
 * ```typescript
 * const categories = getAllMainCategories(); // [1, 2, 3, ..., 17]
 * ```
 */
export function getAllMainCategories(): BvedMainCategory[] {
	return Object.values(BvedMainCategory).filter((value) => typeof value === 'number') as BvedMainCategory[];
}

/**
 * Ermittelt die minimale und maximale Kategorie-Nummer dynamisch
 * @returns Objekt mit min und max Werten
 *
 * @example
 * ```typescript
 * const { min, max } = getMainCategoryRange(); // { min: 1, max: 17 }
 * ```
 */
export function getMainCategoryRange(): { min: number; max: number } {
	const categories = getAllMainCategories();
	return {
		min: Math.min(...categories),
		max: Math.max(...categories),
	};
}

/**
 * Prüft ob eine Nummer eine gültige Hauptkategorie ist (dynamisch)
 * @param categoryNumber Die zu prüfende Nummer
 * @returns true wenn die Nummer im gültigen Bereich liegt
 *
 * @example
 * ```typescript
 * isValidMainCategory(6)  // returns true
 * isValidMainCategory(18) // returns false
 * isValidMainCategory(0)  // returns false
 * ```
 */
export function isValidMainCategory(categoryNumber: number): boolean {
	if (!Number.isInteger(categoryNumber)) {
		return false;
	}
	const { min, max } = getMainCategoryRange();
	return categoryNumber >= min && categoryNumber <= max;
}

/**
 * Extrahiert die Hauptkategorie-Nummer aus einem BVED-Key
 * @param key Der BVED-Key im Format "x.y.z" (z.B. "6.2.16")
 * @returns Die Hauptkategorie-Nummer oder null bei ungültigem Key
 *
 * @example
 * ```typescript
 * getMainCategoryFromKey("6.2.16")  // returns 6
 * getMainCategoryFromKey("1.0.1")   // returns 1
 * getMainCategoryFromKey("invalid") // returns null
 * getMainCategoryFromKey("")        // returns null
 * ```
 */
export function getMainCategoryFromKey(key: string): BvedMainCategory | null {
	if (!key || typeof key !== 'string') {
		return null;
	}

	const parts = key.split('.');
	if (parts.length === 0) {
		return null;
	}

	const mainCategoryNumber = parseInt(parts[0], 10);

	if (isNaN(mainCategoryNumber) || !isValidMainCategory(mainCategoryNumber)) {
		return null;
	}

	return mainCategoryNumber as BvedMainCategory;
}

/**
 * Gibt den i18n Übersetzungsschlüssel für eine Hauptkategorie zurück
 * @param mainCategory Die Hauptkategorie
 * @returns Der i18n Key im Format "billing.bvedMainCategories.CATEGORY_X"
 *
 * @example
 * ```typescript
 * getMainCategoryI18nKey(BvedMainCategory.COMBINED_HEATING_HOT_WATER)
 * // returns "billing.bvedMainCategories.CATEGORY_6"
 *
 * getMainCategoryI18nKey(null)
 * // returns ""
 * ```
 */
export function getMainCategoryI18nKey(mainCategory: BvedMainCategory | null): string {
	if (mainCategory === null || mainCategory === undefined) {
		return '';
	}
	return `billing.bvedMainCategories.CATEGORY_${mainCategory}`;
}
